import React, { Component } from 'react';

import appSettings from '../../config/settings';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

export const LOADABLE_COMPONENT_RELOAD_STORAGE_KEY = 'loadable-component-reload-attempted';

const loadableErrorFragments = [
  'chunkloaderror',
  'loading chunk',
  'failed to fetch dynamically imported module',
  'importing a module script failed',
  'error loading dynamically imported module',
];

const getErrorText = error => {
  return [
    error?.name,
    error?.message,
    error?.cause?.name,
    error?.cause?.message,
    typeof error?.toString === 'function' ? error.toString() : null,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const getSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

export const isRecoverableLoadableError = error => {
  const errorText = getErrorText(error);
  return loadableErrorFragments.some(fragment => errorText.includes(fragment));
};

export const clearRecoverableLoadableErrorReload = (storage = getSessionStorage()) => {
  try {
    storage?.removeItem(LOADABLE_COMPONENT_RELOAD_STORAGE_KEY);
  } catch (e) {
    // Ignore storage access errors and keep the recovery UI available.
  }
};

export const reloadAfterRecoverableLoadError = (
  storage = getSessionStorage(),
  reload = typeof window !== 'undefined' ? () => window.location.reload() : null
) => {
  if (!storage || !reload) {
    return false;
  }

  try {
    const hasRetried = storage.getItem(LOADABLE_COMPONENT_RELOAD_STORAGE_KEY) === 'true';
    if (hasRetried) {
      return false;
    }

    storage.setItem(LOADABLE_COMPONENT_RELOAD_STORAGE_KEY, 'true');
    reload();
    return true;
  } catch (e) {
    return false;
  }
};

// Use ErrorBoundary to catch chunk and dynamic import failures.
// https://reactjs.org/docs/error-boundaries.html
class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidMount() {
    if (!this.state.error) {
      clearRecoverableLoadableErrorReload();
    }
  }

  componentDidCatch(error) {
    if (isRecoverableLoadableError(error)) {
      reloadAfterRecoverableLoadError();
    }
  }

  render() {
    if (this.state.error && isRecoverableLoadableError(this.state.error)) {
      return <LoadableComponentErrorBoundaryPage />;
    }

    return this.props.children;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
